import { cn } from '@lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

const badgeVariants = cva(
  'inline-flex items-center text-center rounded-sm border px-2.5 py-1.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 gap-1',
  {
    variants: {
      variant: {
        active: 'border-transparent bg-green-100 text-green-700 hover:bg-green-200',
        admin: 'border-transparent bg-red-100 text-red-700 hover:bg-red-200',
        approved: 'border-transparent bg-cyan-100 text-cyan-700 hover:bg-cyan-200',
        blocked: 'border-transparent bg-rose-100 text-rose-700 hover:bg-rose-200',
        // Classification markings use tinted fills with colored text to avoid white text on saturated badges.
        'capco-unclassified':
          'border-classification-unclassified-border bg-classification-unclassified-bg text-classification-unclassified-text hover:bg-classification-unclassified-hover',
        'capco-cui':
          'border-classification-cui-border bg-classification-cui-bg text-classification-cui-text hover:bg-classification-cui-hover',
        'capco-secret':
          'border-classification-secret-border bg-classification-secret-bg text-classification-secret-text hover:bg-classification-secret-hover',
        'capco-top-secret':
          'border-classification-top-secret-border bg-classification-top-secret-bg text-classification-top-secret-text hover:bg-classification-top-secret-hover',
        default: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200',
        destructive: 'border-red-500/20 border bg-red-100 text-red-700 hover:bg-red-200',
        expert: 'border-transparent bg-teal-100 text-teal-700 hover:bg-teal-200',
        innovator: 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-200',
        intelligence: 'border-transparent bg-purple-100 text-purple-700 hover:bg-purple-200',
        outline: 'border-gray-200 bg-white text-foreground',
        pending: 'border-transparent bg-amber-100 text-amber-700 hover:bg-amber-200',
        'persona-decision-maker': 'border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
        'persona-librarian': 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-200',
        'persona-mission-engineer': 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-200',
        'persona-program-admin': 'border-transparent bg-purple-100 text-purple-700 hover:bg-purple-200',
        primary: 'text-[#3D2EFF] bg-[#3D2EFF1A]',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        // Workflow status hues used by study/campaign status badges.
        'status-draft': 'border-transparent bg-slate-100 text-slate-700 hover:bg-slate-200',
        'status-proposed': 'border-transparent bg-sky-100 text-sky-700 hover:bg-sky-200',
        'status-approved': 'border-transparent bg-green-100 text-green-700 hover:bg-green-200',
        'status-archived': 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-200',
        'status-historical': 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-200',
        success: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-500/20',
        unapproved: 'border-transparent bg-slate-100 text-slate-600 hover:bg-slate-200',
        warning: 'border-yellow-500/20 border bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      },
      size: {
        lg: 'px-3 py-2 text-base rounded-lg',
        md: 'px-2 py-1 text-sm',
        sm: 'px-2 py-1 text-xs',
        xs: 'px-1.5 py-0.5 text-xs',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'default',
    },
  },
);

export type BadgeProps = React.ComponentProps<'div'> & VariantProps<typeof badgeVariants>;

const Badge = (props: BadgeProps) => {
  const { className, size, variant, ...otherProps } = props;

  return (
    <div
      className={cn(badgeVariants({ size, variant }), className)}
      {...otherProps}
    />
  );
};

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
